import axios from 'axios';
import API from '../config/API';

/**
 * Duplicate clients (BE docs/client-duplicates.md §C–§F). The ONLY file that
 * knows the wire shapes: components read the normalised objects below, so a
 * contract change is a change here.
 *
 *   GET  /v2/client-duplicates?page&pageSize&fresh    → suggestions (paged)
 *   GET  /v2/client-duplicates/dismissed              → dismissed pairs
 *   POST /v2/client-duplicates/merge                  → { groups:[{ keepUserId, mergeUserIds, picks }], idempotencyKey }
 *   POST /v2/client-duplicates/dismiss | /restore     → { userIds }
 *   POST /v2/client-duplicates/undo                   → { batchId }
 *   POST /v2/user-merge/:id/rollback                  → one merge (client panel)
 *   GET  /v2/client-duplicates/recent/:clientUserId   → { merges:[…], undoUntil }
 */

function _authHeaders() {
    return {
        'Content-Type': 'application/json',
        'accessToken': localStorage.getItem('accessToken') || '',
    };
}

// Error envelopes come back as HTTP 200 { statusCode: 0, error }.
function _data(res) {
    const d = res && res.data;
    if (d && d.statusCode === 0) {
        const e = new Error('request_failed');
        e.code = (d.error && (d.error.code || d.error.errorKey)) || 'invalid';
        throw e;
    }
    return (d && (d.responseData || d.data)) || {};
}

function _err(err) {
    if (err && err.code && err.message === 'request_failed') return err;
    const body = err && err.response && err.response.data;
    const e = new Error('request_failed');
    e.code = (body && body.error && body.error.code) || (err && err.response && err.response.status === 429 ? 'rate_limited' : 'invalid');
    return e;
}

const s = (v) => (v == null ? '' : String(v));
const PICK_KEYS = ['name', 'email', 'phoneNumber', 'dob'];

export function normaliseMember(m, fieldVals) {
    m = m || {};
    const own = (fieldVals && fieldVals[s(m.userId || m._id)]) || {};
    const name = s(m.name).trim() || [s(m.first_name), s(m.last_name)].join(' ').trim() || s(own.name);
    return {
        userId: s(m.userId || m._id),
        name: name || 'Unnamed client',
        email: s(m.email) || s(own.email),
        phoneNumber: s(m.phone || m.phoneNumber) || s(own.phoneNumber),
        dob: s(m.dob) || s(own.dob),
        hasLogin: m.hasLogin === true,
        bookingCount: Number(m.bookings != null ? m.bookings : m.bookingCount) || 0,
        lastBookingDate: m.lastBookingAt || m.lastBookingDate || null,
    };
}

function _signal(g) {
    const sig = Array.isArray(g.signals) ? g.signals : (g.signal ? [g.signal] : []);
    if (sig.includes('email') && sig.includes('phone')) return 'email_phone';
    return sig[0] || null;   // null: not known (shown as "Same details")
}

export function normaliseGroup(g) {
    g = g || {};
    // fields.<key>.options = [{ value, userId }]: each member's own value.
    const fieldVals = {};
    PICK_KEYS.forEach((k) => {
        const f = g.fields && g.fields[k];
        ((f && f.options) || []).forEach((o) => {
            const id = s(o.userId);
            if (!id) return;
            fieldVals[id] = fieldVals[id] || {};
            fieldVals[id][k] = o.value;
        });
    });
    const members = (g.members || g.clients || []).map((m) => normaliseMember(m, fieldVals));
    const keep = s(g.keepUserId) || (members[0] && members[0].userId) || '';
    return {
        groupKey: s(g.groupKey),
        signal: _signal(g),                        // 'email' | 'phone' | 'email_phone' | null
        confidence: g.confidence === 'strong' ? 'strong' : 'possible',
        labels: Array.isArray(g.labels) ? g.labels.map(s) : [],   // e.g. 'different_surname'
        keepUserId: keep,
        members,
        dismissedAt: g.dismissedAt || null,
    };
}

const PAGE_SIZE = 50;

function list({ dismissed = false, fresh = false } = {}) {
    if (dismissed) {
        return axios.get(API.CLIENT_DUPLICATES + '/dismissed', { headers: _authHeaders() })
            .then(_data, (e) => { throw _err(e); })
            .then((d) => ({
                enabled: d.enabled === true,
                groups: (d.pairs || []).map((p) => normaliseGroup({
                    groupKey: (p.userIds || []).map(s).sort().join(':'),
                    // Older APIs send names only; newer ones the contact details
                    // and what the pair shares today.
                    members: Array.isArray(p.members) && p.members.length
                        ? p.members
                        : (p.userIds || []).map((id, i) => ({ userId: id, name: (p.names || [])[i] })),
                    signals: p.signals,
                    dismissedAt: p.at ? new Date(p.at).toISOString() : null,
                })),
            }));
    }
    // A salon's whole list, page by page (pageSize ≤ 50 server-side).
    const page = (n, acc) => axios.get(API.CLIENT_DUPLICATES, { headers: _authHeaders(), params: { page: n, pageSize: PAGE_SIZE, fresh: fresh && n === 1 ? true : undefined } })
        .then(_data, (e) => { throw _err(e); })
        .then((d) => {
            const all = acc.concat(d.groups || []);
            const more = d.enabled === true && n * PAGE_SIZE < (Number(d.groupCount) || 0) && n < 10;
            if (more) return page(n + 1, all);
            return {
                enabled: d.enabled === true,
                // every merge moves loyalty, gift cards, forms, … (step A move-set)
                movesEverything: d.movesEverything === true,
                count: Number(d.count) || 0,
                groups: all.map(normaliseGroup),
                hasMore: false,
            };
        });
    return page(1, []);
}

function merge(groups, idempotencyKey) {
    return axios.post(API.CLIENT_DUPLICATES + '/merge', { groups, idempotencyKey }, { headers: _authHeaders() })
        .then(_data, (e) => { throw _err(e); })
        .then((d) => ({
            batchId: s(d.batchId),
            results: (d.groups || []).map((r) => ({
                groupKey: s(r.groupKey),
                ok: r.status === 'merged',
                mergeIds: (r.mergeIds || []).map(s),
                code: s(r.code),
            })),
        }));
}

const _ids = (g) => (g && g.members ? g.members.map((m) => m.userId) : []);

function dismiss(group) {
    return axios.post(API.CLIENT_DUPLICATES + '/dismiss', { userIds: _ids(group) }, { headers: _authHeaders() })
        .then(_data, (e) => { throw _err(e); });
}

function restore(group) {
    return axios.post(API.CLIENT_DUPLICATES + '/restore', { userIds: _ids(group) }, { headers: _authHeaders() })
        .then(_data, (e) => { throw _err(e); });
}

/**
 * Undo a batch ({ batchId }: the toast's Undo all) or specific merges
 * ({ mergeIds }, newest first: the client panel line, through the per-merge
 * rollback). Results: [{ ok, code }].
 */
async function undo(target) {
    if (target && Array.isArray(target.mergeIds)) {
        const results = [];
        for (const id of target.mergeIds) {
            try {
                const res = await axios.post(API.USER_MERGE_ROLLBACK + id + '/rollback', {}, { headers: _authHeaders() });
                _data(res);
                results.push({ mergeId: id, ok: true, code: '' });
            } catch (e) {
                results.push({ mergeId: id, ok: false, code: s((e && e.code) || 'invalid') });
                break;
            }
        }
        return { results };
    }
    return axios.post(API.CLIENT_DUPLICATES + '/undo', target, { headers: _authHeaders() })
        .then(_data, (e) => { throw _err(e); })
        .then((d) => ({
            results: (d.groups || []).map((r) => ({ groupKey: s(r.groupKey), ok: r.status === 'undone', code: s(r.code) })),
        }));
}

/** This salon's undoable merges into the client, or null (also on an older API). */
function recentForClient(clientUserId) {
    if (!clientUserId) return Promise.resolve(null);
    return axios.get(API.CLIENT_DUPLICATES + '/recent/' + clientUserId, { headers: _authHeaders() })
        .then(_data)
        .then((d) => {
            const merges = (d && d.merges) || [];
            if (!merges.length) return null;
            const first = s(merges[0].mergedWithName) || 'another record';
            return {
                mergeIds: merges.map((m) => s(m.mergeId)),
                mergedWithName: merges.length > 1 ? `${first} and ${merges.length - 1} other${merges.length > 2 ? 's' : ''}` : first,
                bookingsMoved: merges.reduce((n, m) => n + (Number(m.bookingsMoved) || 0), 0),
                notesMoved: 0,
                undoUntil: d.undoUntil || null,
            };
        })
        .catch(() => null);
}

/** Is duplicate-finding on for this viewer? No scan (the "Find duplicates" button). */
function status() {
    return axios.get(API.CLIENT_DUPLICATES, { headers: _authHeaders(), params: { status: 1 } })
        .then(_data)
        .then((d) => !!(d && d.enabled === true))
        .catch(() => false);
}

export const clientDuplicatesService = { list, merge, dismiss, restore, undo, recentForClient, status };
